import React from 'react';

import { FormikValues } from 'formik';
import { Typography } from '@mui/material';

import {
  cancelSubscription,
  ConfirmationTypes,
  PharmacyBillingPageStatuses,
  pharmacyBillingSelector,
  resumeSubscription,
} from '@pharmaplan/common';

import DeactivateDialog from '../../../../../Admin/PharmacistList/DeactivateDialog';
import {
  DeactivateModalIcon,
  OutcomeModalTypes,
} from '../../../../../../helpers/Constants';
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch';
import {
  resetDialog,
  setDialog,
} from '../../../../../../reducers/dialogReducer';
import ConfirmationModal from '../../../../../Modals/ConfirmationModal';
import { useAppSelector } from '../../../../../../hooks/useAppSelector';
import OutcomeModal from '../../../../../Modals/OutcomeModal';
import strings from '../../../../../../localization';

import styles from './styles';
import ReasonDialog from './ReasonDialog';

interface ISubscriptionDialogs {
  setPageStatus?: React.Dispatch<
    React.SetStateAction<PharmacyBillingPageStatuses>
  >;
}

const SubscriptionDialogs = ({ setPageStatus }: ISubscriptionDialogs) => {
  const dispatch = useAppDispatch();
  const billing = useAppSelector(pharmacyBillingSelector);
  const { isEligibleForRefund, neverSubscribed } = billing ?? {};

  const downgrade = (values: FormikValues) => {
    const { reason } = values ?? {};
    dispatch(cancelSubscription({ reason }));
  };

  const showPlans = () => {
    setPageStatus?.(PharmacyBillingPageStatuses.showPaymentPlans);
    dispatch(resetDialog());
  };

  const goToExistingPayment = () => {
    dispatch(resetDialog());
    setPageStatus?.(PharmacyBillingPageStatuses.existingPaymentPlan);
  };

  const goToUpgrade = () => {
    dispatch(resetDialog());
    setPageStatus?.(PharmacyBillingPageStatuses.upgradePlan);
  };

  const cancellationWithinTenDays = () => {
    dispatch(
      setDialog({
        Component: (
          <ReasonDialog
            confirmationMessage={strings.areYouSureCancelUnlimited}
            heading={strings.cancelSubscription}
            onSubmit={downgrade}
            primaryPress={showPlans}
          />
        ),
        showCloseButton: true,
        heading: {
          title: '',
        },
      }),
    );
  };

  const cancellationFromView = () => {
    dispatch(
      setDialog({
        Component: (
          <DeactivateDialog
            id=""
            confirmationMessage={strings.areYouSureCancelUnlimited}
            heading={strings.cancelSubscription}
            iconType={DeactivateModalIcon.warning}
            onSubmit={downgrade}
          />
        ),
        showCloseButton: true,
        heading: {
          title: '',
        },
      }),
    );
  };

  const showCancelDialog = () => {
    dispatch(
      setDialog({
        Component: (
          <ConfirmationModal
            customClose={() =>
              downgrade({ reason: '' })}
            confirmAction={showPlans}
            customMessage={(
              <Typography sx={styles.cancelSubscriptionMessage}>
                {strings.areYouSureCancelUnlimitedWithNote}
              </Typography>
            )}
            type={ConfirmationTypes.cancelSubscription}
          />
        ),
        showCloseButton: false,
        heading: {
          title: '',
        },
      }),
    );
  };

  const showOnPlanToUpgrade = () => {
    dispatch(
      setDialog({
        Component: (
          <OutcomeModal
            type={OutcomeModalTypes.firstTimeUpgrade}
            message={strings.becauseThisIsFirstTime}
            customClose={goToUpgrade}
            showCloseButton
            customCloseLabel={strings.continue}
          />
        ),
        showCloseButton: false,
        heading: {
          title: '',
        },
      }),
    );
  };

  const showOnUpgrade = (confirmAction: () => void) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <ConfirmationModal
              type={ConfirmationTypes.planToUpgrade}
              confirmAction={confirmAction}
              customMessage={(
                <Typography sx={styles.cancelSubscriptionMessage}>
                  {strings.subscriptionWillBeActiveTillLastDay}
                </Typography>
            )}
              customClose={goToExistingPayment}
            />
          ),
          showCloseButton: false,
          heading: {
            title: '',
          },
        }),
      );
    };

  const reactivateSubscription = () => {
    dispatch(resumeSubscription());
  };

  const reactivateDialog = () => {
    dispatch(
      setDialog({
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.reactivateSubscription}
            confirmAction={reactivateSubscription}
            customClose={goToExistingPayment}
          />
        ),
        showCloseButton: false,
        heading: {
          title: '',
        },
      }),
    );
  };

  return {
    reactivateDialog,
    showOnPlanToUpgrade: neverSubscribed ? showOnPlanToUpgrade : goToUpgrade,
    showOnUpgrade,
    cancelFromCompare: isEligibleForRefund
      ? cancellationFromView
      : () =>
        downgrade({ reason: '' }),
    showCancelDialog: isEligibleForRefund
      ? cancellationWithinTenDays
      : showCancelDialog,
  };
};

export default SubscriptionDialogs;
